import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { AppMember } from '@/lib/app-records';
import { outreachAction } from '@/lib/outreach';
import { ImportMenu } from './ImportMenu';
import { type ImportSource, TableImportDrawer } from './TableImportDrawer';
import { campaignCounts } from './campaign-counts';
import type { OutreachData } from './useOutreach';

interface OutreachCampaignListProps {
  appId: string;
  members: AppMember[];
  data: OutreachData;
  canImport: boolean;
  onOpen: (sequenceId: string) => void;
}

const field = 'w-64 rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-1.5 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none';

/**
 * The campaigns of an app (04/11/2026), as lemlist lists them: one line each,
 * its sender, its prospects, what came back. A list is made by importing
 * (into a new campaign) or by naming a campaign, then filling it from the list.
 */
export const OutreachCampaignList = ({ appId, members, data, canImport, onOpen }: OutreachCampaignListProps) => {
  const [naming, setNaming] = useState<string | null>(null);
  const [importing, setImporting] = useState<ImportSource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const campaigns = data.state?.campaigns ?? [];
  const nameOf = (userId: string) => members.find((member) => member.userId === userId)?.name ?? '';

  const create = async () => {
    const name = naming?.trim();
    if (!name) { setNaming(null); return; }
    try {
      const answer = await outreachAction<{ sequenceId: string }>({ action: 'create_campaign', toolAppId: appId, name });
      setNaming(null);
      await data.reload();
      onOpen(answer.sequenceId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Campagne non créée.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {naming === null
          ? <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => setNaming('')}><Plus className="h-4 w-4" />Nouvelle campagne</Button>
          : (
            <input autoFocus aria-label="Nom de la campagne" placeholder="Nom de la campagne" value={naming} maxLength={120} className={field}
              onChange={(event) => setNaming(event.target.value)} onBlur={() => { void create(); }}
              onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') setNaming(null); }} />
          )}
        {canImport && <ImportMenu primary onChoose={setImporting} />}
      </div>
      {error && <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>}

      {campaigns.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-domino-cardBorder bg-domino-white">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="border-b border-domino-cardBorder text-xs text-domino-inkSoft">
              <tr>
                <th className="px-4 py-2 font-medium">Campagne</th>
                <th className="px-4 py-2 font-medium">Expéditeur</th>
                <th className="px-4 py-2 text-right font-medium">Prospects</th>
                <th className="px-4 py-2 text-right font-medium">Contactés</th>
                <th className="px-4 py-2 text-right font-medium">Réponses</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => {
                const counts = campaignCounts(data.state?.enrollments.filter((enrollment) => enrollment.sequenceId === campaign.id) ?? []);
                return (
                  <tr key={campaign.id} className="cursor-pointer border-b border-domino-cardBorder/60 last:border-0 hover:bg-domino-warm" onClick={() => onOpen(campaign.id)}>
                    <td className="px-4 py-2.5">
                      <button type="button" className="font-semibold text-domino-ink hover:text-domino-aubergine" onClick={(event) => { event.stopPropagation(); onOpen(campaign.id); }}>
                        {campaign.name}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-domino-inkSoft">{campaign.ownerUserId === data.userId ? 'Moi' : nameOf(campaign.ownerUserId)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-domino-ink">{campaign.recordIds.length}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-domino-ink">{counts.contacted}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-domino-ink">{counts.replied}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {importing && (
        <TableImportDrawer toolAppId={appId} source={importing} onClose={() => setImporting(null)}
          onImported={() => { void data.reload(); }} onOpenCampaign={onOpen} />
      )}
    </div>
  );
};
